import React, { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Stack,
  Chip,
  IconButton,
  Alert,
  Switch,
  Grid,
  Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { ruleEngineService } from 'src/services/ruleEngine.service';
import { ToastNotifications } from 'src/components';
import type { TransactionRule, CreateRuleDTO } from 'src/services/ruleEngine.service';

interface TransactionRulesManagerProps {
  studentId: string;
}

const RULE_PRESETS = [
  {
    name: 'Pocket Purchases Auto-Pay',
    description: 'Automatically approves any classroom store purchase under $5.00 so you never have to wait in line.',
    rule_type: 'amount' as const,
    operator: 'less_than' as const,
    amount: 5,
    action: 'auto_pay' as const,
    icon: 'solar:bolt-bold-duotone',
  },
  {
    name: 'Wallet Safety Floor Shield',
    description: 'Protects your pocket money by pausing deductions if your balance would drop below $10.00.',
    rule_type: 'balance' as const,
    min_balance_after: 10,
    action: 'defer' as const,
    icon: 'solar:shield-check-bold-duotone',
  },
  {
    name: 'Store Items Under $10 Auto-Pay',
    description: 'Auto-approves everyday school supplies and snacks under $10.00.',
    rule_type: 'amount' as const,
    operator: 'less_than' as const,
    amount: 10,
    action: 'auto_pay' as const,
    icon: 'solar:bag-heart-bold-duotone',
  },
];

export default function TransactionRulesManager({
  studentId,
}: TransactionRulesManagerProps): React.JSX.Element {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [ruleType, setRuleType] = useState<'amount' | 'balance'>('amount');
  const [amountValue, setAmountValue] = useState<string>('5');
  const [minBalanceAfter, setMinBalanceAfter] = useState<string>('10');
  const [ruleAction, setRuleAction] = useState<'auto_pay' | 'defer' | 'deny'>('auto_pay');

  const queryClient = useQueryClient();

  // Fetch student rules
  const {
    data: rules = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['student-rules', studentId],
    queryFn: () => ruleEngineService.getStudentRules(studentId),
    enabled: !!studentId,
  });

  // Mutations
  const createRuleMutation = useMutation({
    mutationFn: (ruleData: CreateRuleDTO) => ruleEngineService.createRule(ruleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-rules', studentId] });
      ToastNotifications.success('Smart Rule enabled!');
      handleCloseModal();
    },
    onError: (err: any) => {
      ToastNotifications.error(err.message || 'Failed to create rule');
    },
  });

  const updateRuleMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      ruleEngineService.updateRule({ id, ...updates }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-rules', studentId] });
      ToastNotifications.success('Rule updated!');
    },
    onError: (err: any) => {
      ToastNotifications.error(err.message || 'Failed to update rule');
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: (ruleId: string) => ruleEngineService.deleteRule(ruleId, studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-rules', studentId] });
      ToastNotifications.success('Rule deleted.');
    },
    onError: (err: any) => {
      ToastNotifications.error(err.message || 'Failed to delete rule');
    },
  });

  const handleCreateRule = () => {
    if (!ruleName.trim()) {
      ToastNotifications.error('Please give your smart rule a name');
      return;
    }

    let conditions: Record<string, any> = {};

    if (ruleType === 'amount') {
      const amt = parseFloat(amountValue);
      if (isNaN(amt) || amt <= 0) {
        ToastNotifications.error('Please enter a valid dollar amount');
        return;
      }
      conditions = {
        operator: 'less_than',
        amount: amt,
        transaction_types: ['debit'],
      };
    } else {
      const minBal = parseFloat(minBalanceAfter);
      if (isNaN(minBal) || minBal < 0) {
        ToastNotifications.error('Please enter a valid minimum balance');
        return;
      }
      conditions = {
        min_balance_after: minBal,
        protection_mode: true,
      };
    }

    const ruleData: CreateRuleDTO = {
      student_id: studentId,
      name: ruleName.trim(),
      description: ruleDescription.trim() || undefined,
      rule_type: ruleType,
      conditions,
      action: ruleAction,
      priority: rules.length + 1,
    };

    createRuleMutation.mutate(ruleData);
  };

  const handleApplyPreset = (preset: typeof RULE_PRESETS[0]) => {
    setRuleName(preset.name);
    setRuleDescription(preset.description);
    setRuleType(preset.rule_type);
    setRuleAction(preset.action);

    if (preset.rule_type === 'amount' && preset.amount) {
      setAmountValue(preset.amount.toString());
    } else if (preset.rule_type === 'balance' && preset.min_balance_after) {
      setMinBalanceAfter(preset.min_balance_after.toString());
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setRuleName('');
    setRuleDescription('');
    setRuleType('amount');
    setAmountValue('5');
    setMinBalanceAfter('10');
    setRuleAction('auto_pay');
  };

  const toggleRuleActive = (rule: TransactionRule) => {
    updateRuleMutation.mutate({
      id: rule.id,
      updates: { is_active: !rule.is_active },
    });
  };

  const deleteRule = (ruleId: string, name: string) => {
    if (window.confirm(`Are you sure you want to remove the "${name}" rule?`)) {
      deleteRuleMutation.mutate(ruleId);
    }
  };

  const formatFriendlyRuleText = (rule: TransactionRule) => {
    if (rule.description) return rule.description;
    if (rule.rule_type === 'amount') {
      return `Automatically approves debit charges under $${rule.conditions?.amount || 0}.`;
    }
    if (rule.rule_type === 'balance') {
      return `Pauses charges if your wallet would drop below $${rule.conditions?.min_balance_after || 0}.`;
    }
    return 'Custom auto-pilot rule';
  };

  const getRuleIcon = (ruleType: string) => {
    if (ruleType === 'amount') return 'solar:bolt-bold-duotone';
    if (ruleType === 'balance') return 'solar:shield-check-bold-duotone';
    return 'solar:settings-bold-duotone';
  };

  if (error) {
    return (
      <Alert severity="error">
        Unable to load your smart rules. Please refresh the page.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Tab Content Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.0625rem' }}>
            Smart Auto-Pilot Rules
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 0.25 }}>
            Turn on smart rules that save or approve payments for you automatically.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<Icon icon="solar:add-circle-bold-duotone" width={16} />}
          onClick={() => setIsCreateModalOpen(true)}
          disabled={rules.length >= 10}
          sx={{
            height: 36,
            borderRadius: 1.5,
            fontWeight: 700,
            fontSize: '0.8125rem',
            textTransform: 'none',
            bgcolor: '#059669',
            color: '#FFFFFF',
            '&:hover': { bgcolor: '#047857' },
          }}
        >
          New Rule
        </Button>
      </Stack>

      {/* Rules List */}
      {isLoading ? (
        <Stack spacing={1.5}>
          {[1, 2].map((i) => (
            <Card key={i} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0', height: 80 }} />
          ))}
        </Stack>
      ) : rules.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2.5,
            border: '1px dashed #CBD5E1',
            bgcolor: '#F8FAFC',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              bgcolor: 'rgba(5, 150, 105, 0.1)',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
            }}
          >
            <Icon icon="solar:bolt-bold-duotone" width={28} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
            No Smart Rules Active
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', maxWidth: 400, mx: 'auto', mt: 0.5, mb: 2 }}>
            Smart rules do the work for you! Turn on rules to auto-approve small store items or protect your wallet balance.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<Icon icon="solar:bolt-bold-duotone" width={16} />}
            onClick={() => setIsCreateModalOpen(true)}
            sx={{
              borderRadius: 1.5,
              fontWeight: 700,
              fontSize: '0.8125rem',
              textTransform: 'none',
              bgcolor: '#059669',
              '&:hover': { bgcolor: '#047857' },
            }}
          >
            Pick a Smart Rule
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {rules.map((rule) => {
            const ruleIcon = getRuleIcon(rule.rule_type);

            return (
              <Grid item xs={12} sm={6} key={rule.id}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2.25,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: rule.is_active ? '#E2E8F0' : '#F1F5F9',
                    bgcolor: rule.is_active ? '#FFFFFF' : '#F8FAFC',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                    gap: 1.5,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#CBD5E1',
                      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
                    },
                  }}
                >
                  <Box>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 1.75,
                            bgcolor: rule.is_active ? 'rgba(5, 150, 105, 0.1)' : 'grey.200',
                            color: rule.is_active ? '#059669' : 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon icon={ruleIcon} width={22} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }} noWrap>
                            {rule.name}
                          </Typography>
                          {rule.times_applied > 0 && (
                            <Chip
                              label={`Triggered ${rule.times_applied}x`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.625rem',
                                fontWeight: 600,
                                bgcolor: 'rgba(5, 150, 105, 0.08)',
                                color: '#059669',
                                borderRadius: 0.75,
                                mt: 0.25,
                              }}
                            />
                          )}
                        </Box>
                      </Stack>

                      <Tooltip title="Delete Rule">
                        <IconButton
                          size="small"
                          onClick={() => deleteRule(rule.id, rule.name)}
                          sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'error.main' } }}
                        >
                          <Icon icon="solar:trash-bin-trash-bold" width={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 1 }}>
                      {formatFriendlyRuleText(rule)}
                    </Typography>
                  </Box>

                  {/* Bottom: Toggle Switch */}
                  <Box sx={{ pt: 1.5, borderTop: '1px solid #F1F5F9' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: rule.is_active ? '#059669' : 'text.secondary', fontWeight: 700, fontSize: '0.75rem' }}>
                        {rule.is_active ? 'Active on Auto-Pilot' : 'Rule Paused'}
                      </Typography>
                      <Switch
                        checked={rule.is_active}
                        onChange={() => toggleRuleActive(rule)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#059669',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#059669',
                            },
                          },
                        }}
                      />
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Smart Rule Modal */}
      <Dialog
        open={isCreateModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(5, 150, 105, 0.12)',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon icon="solar:bolt-bold-duotone" width={20} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
                Set Up a Smart Rule
              </Typography>
            </Stack>
            <IconButton size="small" onClick={handleCloseModal}>
              <Icon icon="solar:close-circle-bold" width={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Preset Recipe Cards */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 1, display: 'block' }}>
            Pick a Smart Preset:
          </Typography>

          <Stack spacing={1} sx={{ mb: 2.5 }}>
            {RULE_PRESETS.map((preset) => (
              <Card
                key={preset.name}
                elevation={0}
                onClick={() => handleApplyPreset(preset)}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: ruleName === preset.name ? '#059669' : '#E2E8F0',
                  bgcolor: ruleName === preset.name ? 'rgba(5, 150, 105, 0.08)' : '#F8FAFC',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: '#059669', transform: 'translateY(-1px)' },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                  <Box sx={{ color: '#059669', mt: 0.25 }}>
                    <Icon icon={preset.icon} width={20} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.primary' }}>
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {preset.description}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            ))}
          </Stack>

          {/* Rule Configuration Fields */}
          <Stack spacing={2}>
            <TextField
              label="Rule Name"
              placeholder="e.g. Small Snacks Auto-Pay"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              fullWidth
              size="small"
              required
            />

            {ruleType === 'amount' ? (
              <TextField
                label="Auto-Approve Charges Under ($D)"
                placeholder="e.g. 5.00"
                type="number"
                value={amountValue}
                onChange={(e) => setAmountValue(e.target.value)}
                fullWidth
                size="small"
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600 }}>$</Typography> }}
              />
            ) : (
              <TextField
                label="Keep Wallet Balance Above ($D)"
                placeholder="e.g. 10.00"
                type="number"
                value={minBalanceAfter}
                onChange={(e) => setMinBalanceAfter(e.target.value)}
                fullWidth
                size="small"
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600 }}>$</Typography> }}
              />
            )}

            <Button
              variant="contained"
              onClick={handleCreateRule}
              disabled={createRuleMutation.isPending || !ruleName.trim()}
              fullWidth
              sx={{
                py: 1.25,
                borderRadius: 1.75,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                bgcolor: '#059669',
                '&:hover': { bgcolor: '#047857' },
              }}
            >
              {createRuleMutation.isPending ? 'Activating Rule...' : 'Enable Smart Rule'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}